import React, { useState } from 'react';
import { useEntityList, createEntity, updateEntity, deleteEntity } from '../data/entities';
import { useAuth } from '../AuthContext';

// Dev Studio: browser VS Code (code-server) on an ephemeral Fargate task with
// the org's datasets mounted read-only at ~/datasets. Create → the devspace
// worker launches it and writes back a URL + one-time password; Stop kills
// the task. Spaces auto-stop at the runtime cap.
const ACTIVE = ['requested', 'launching', 'running'];

export default function WorkspaceDevSpaces({ orgId, workspaceId, compact = false }) {
  const { user } = useAuth();
  const scope = { org: orgId, workspace: workspaceId };
  const { docs: spaces } = useEntityList('devSpace', scope);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  return (
    <section style={{ marginTop: compact ? 12 : 28 }}>
      {!compact && (
        <>
          <h2 style={{ margin: 0 }}>Dev Studio</h2>
          <p className="cs-help" style={{ marginTop: 4 }}>
            VS Code in the browser on an isolated Fargate task — datasets mounted
            at <code>~/datasets</code>, auto-stops after the runtime cap.
          </p>
        </>
      )}

      <form
        style={{ display: 'flex', gap: 8, marginTop: 12, maxWidth: 480 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          setError('');
          try {
            await createEntity('devSpace', scope, { name: name.trim(), requestedBy: user.uid });
            setName('');
          } catch (err) {
            setError(err.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <input
          className="cs-input"
          placeholder="New dev space name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">
          Launch
        </button>
      </form>
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 8 }}>{error}</p>}

      {spaces && !spaces.length && (
        <div className="cs-empty" style={{ marginTop: 14 }}>No dev spaces yet.</div>
      )}
      <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
        {(spaces || []).map((s) => (
          <div key={s.id} className="cs-card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong>{s.name}</strong>
            <span className="cs-help" style={{ fontSize: 12 }}>{s.status}</span>
            <span style={{ flex: 1 }} />
            {s.status === 'running' && s.url && (
              <>
                <a className="cs-btn cs-btn-primary cs-btn-sm" href={s.url} target="_blank" rel="noreferrer">
                  Open VS Code
                </a>
                <code
                  className="cs-help"
                  style={{ fontSize: 12, cursor: 'pointer' }}
                  title="Click to copy password"
                  onClick={() => navigator.clipboard?.writeText(s.password || '')}
                >
                  pw: {s.password}
                </code>
              </>
            )}
            {ACTIVE.includes(s.status) && (
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={() => updateEntity('devSpace', scope, s.id, { stopRequested: true })}
              >
                Stop
              </button>
            )}
            {!ACTIVE.includes(s.status) && (
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={async () => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm(`Delete dev space "${s.name}"?`)) {
                    await deleteEntity('devSpace', scope, s.id);
                  }
                }}
              >
                Delete
              </button>
            )}
            {s.error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', width: '100%', margin: 0 }}>{s.error}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
